import { create } from 'zustand';
import { LEVEL_CAP, xpCumulativeFor } from '../data/progression.config';
import type { TalentNodeId } from '../types/talent.types';

const STORAGE_KEY = 'usagi_character_progression';

/**
 * Persistent long-term progression (no per-run reset). `pointsSpent` is
 * serialized as a string[] treated as a set.
 */
interface CharacterProgressionState {
  level: number;
  xp: number;
  talentPoints: number;
  pointsSpent: TalentNodeId[];
  bossRewardsClaimed: string[];
}

interface CharacterProgressionActions {
  /**
   * Each level-up grants 1 talent point. At LEVEL_CAP, XP keeps accumulating
   * but no longer triggers level-ups.
   */
  gainXp: (amount: number) => void;
  /**
   * Decrements `talentPoints`. Validation (adjacency, budget, duplicate) is the
   * caller's responsibility (cf. `TalentTreeService.spendPoint`).
   */
  spendPoint: (nodeId: TalentNodeId) => void;
  /** The caller handles the respec cost. */
  refundAllPoints: () => void;
  /**
   * Marks a boss reward as claimed. Idempotent — returns `false` (no-op) if
   * already claimed. The caller (`ContentUnlockService`) delivers the grants.
   */
  claimBossReward: (bossId: string) => boolean;
  /** Restore state from an external save (SaveService). */
  restore: (state: CharacterProgressionState) => void;
  /** Full reset (dev tool / new character). */
  reset: () => void;
}

const DEFAULT_STATE: CharacterProgressionState = {
  level: 1,
  xp: 0,
  talentPoints: 0,
  pointsSpent: [],
  bossRewardsClaimed: [],
};

function load(): CharacterProgressionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CharacterProgressionState>;
      return {
        level: parsed.level ?? DEFAULT_STATE.level,
        xp: parsed.xp ?? DEFAULT_STATE.xp,
        talentPoints: parsed.talentPoints ?? DEFAULT_STATE.talentPoints,
        pointsSpent: parsed.pointsSpent ?? [],
        bossRewardsClaimed: parsed.bossRewardsClaimed ?? [],
      };
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_STATE;
}

function persist(state: CharacterProgressionState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      level: state.level,
      xp: state.xp,
      talentPoints: state.talentPoints,
      pointsSpent: state.pointsSpent,
      bossRewardsClaimed: state.bossRewardsClaimed,
    }),
  );
}

export const useCharacterProgressionStore = create<
  CharacterProgressionState & CharacterProgressionActions
>((set, get) => ({
  ...load(),

  gainXp: (amount) => {
    if (amount <= 0) {
      return;
    }
    set((s) => {
      const newXp = s.xp + amount;
      let newLevel = s.level;
      let gained = 0;
      while (newLevel < LEVEL_CAP && newXp >= xpCumulativeFor(newLevel + 1)) {
        newLevel++;
        gained++;
      }
      const next: CharacterProgressionState = {
        ...s,
        xp: newXp,
        level: newLevel,
        talentPoints: s.talentPoints + gained,
      };
      persist(next);
      return next;
    });
  },

  spendPoint: (nodeId) => {
    set((s) => {
      if (s.talentPoints <= 0 || s.pointsSpent.includes(nodeId)) {
        return s;
      }
      const next: CharacterProgressionState = {
        ...s,
        talentPoints: s.talentPoints - 1,
        pointsSpent: [...s.pointsSpent, nodeId],
      };
      persist(next);
      return next;
    });
  },

  refundAllPoints: () => {
    set((s) => {
      const next: CharacterProgressionState = {
        ...s,
        talentPoints: s.talentPoints + s.pointsSpent.length,
        pointsSpent: [],
      };
      persist(next);
      return next;
    });
  },

  claimBossReward: (bossId) => {
    const state = get();
    if (state.bossRewardsClaimed.includes(bossId)) {
      return false;
    }
    set((s) => {
      const next: CharacterProgressionState = {
        ...s,
        bossRewardsClaimed: [...s.bossRewardsClaimed, bossId],
      };
      persist(next);
      return next;
    });
    return true;
  },

  restore: (state) => {
    set(state);
    persist(state);
  },

  reset: () => {
    set(DEFAULT_STATE);
    persist(DEFAULT_STATE);
  },
}));
